import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../run.css";
import { repo } from "../ch/planrepo";
import { TrainingPlan } from "types/app";
import { validateOnboarding } from "../ch/onboarding2Validation";
import { encodeLongRunDay, encodeTrainingDays } from "../ch/shareableConfig";
import type { dayOfWeek } from "types/app";
import { TopNav } from "./TopNav";
import { SportConfig } from "../ch/sportConfig";
import onboardingDistance from "../img/onboarding/onboardingDistance.png";
import onboardingRacedate from "../img/onboarding/onboardingRacedate.jpg";
import onboardingTime from "../img/onboarding/onboardingTime.jpg";

// Show 3 images for the visual side on larger screens
const ONBOARDING_IMAGES = [onboardingDistance, onboardingTime, onboardingRacedate];

type OnboardingData = {
  distance: string;
  raceDate: Date | null;
  trainingDays: string[];
  longRunDay: string;
  goalTime: string;
};

const DAYS_OF_WEEK = [
  { full: "Monday", abbr: "Mon" },
  { full: "Tuesday", abbr: "Tue" },
  { full: "Wednesday", abbr: "Wed" },
  { full: "Thursday", abbr: "Thu" },
  { full: "Friday", abbr: "Fri" },
  { full: "Saturday", abbr: "Sat" },
  { full: "Sunday", abbr: "Sun" },
];

interface SportOnboardingProps {
  sportConfig: SportConfig;
  navKey: string;
}

const SportOnboarding = ({ sportConfig, navKey }: SportOnboardingProps) => {
  const navigate = useNavigate();

  // Use the same default date the calendar visually highlights.
  const defaultRaceDate = new Date(
    new Date().setMonth(new Date().getMonth() + 2),
  );

  const [formData, setFormData] = useState<OnboardingData>({
    distance: "",
    raceDate: defaultRaceDate,
    trainingDays: [],
    longRunDay: "",
    goalTime: "",
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<TrainingPlan | null>(null);
  const [recommendedDays, setRecommendedDays] = useState<number>(0);

  // Change image based on scroll position
  useEffect(() => {
    const computeIndex = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrollableHeight = Math.max(1, documentHeight - windowHeight);
      const raw = scrollPosition / scrollableHeight;
      const clamped = Math.max(0, Math.min(1, raw));

      const idx = Math.min(
        Math.round(clamped * (ONBOARDING_IMAGES.length - 1)),
        ONBOARDING_IMAGES.length - 1,
      );
      setCurrentImageIndex(idx);
    };

    computeIndex();

    window.addEventListener("scroll", computeIndex, { passive: true });
    window.addEventListener("resize", computeIndex);
    return () => {
      window.removeEventListener("scroll", computeIndex);
      window.removeEventListener("resize", computeIndex);
    };
  }, []);

  // Load the selected plan when distance and goalTime change
  useEffect(() => {
    const loadPlan = async () => {
      if (!formData.distance || !formData.goalTime) {
        setSelectedPlan(null);
        setRecommendedDays(0);
        return;
      }

      const planId = `${formData.distance}-${formData.goalTime}`;

      try {
        const planSummary = repo.find(planId);
        const plan = await repo.fetch(planSummary, sportConfig.sport);
        setSelectedPlan(plan);

        // Required days should reflect the busiest week (max non-rest workouts across the plan)
        if (plan.schedule && plan.schedule.length > 0) {
          const countWeek = (week: any): number => {
            const workouts = week?.workouts;
            if (!Array.isArray(workouts)) return 0;
            return workouts.filter((workout: any) => {
              const title = String(workout?.title ?? "").toLowerCase();
              return title && title !== "rest" && !title.includes("rest");
            }).length;
          };

          const busiest = plan.schedule.reduce((max: number, week: any) => {
            return Math.max(max, countWeek(week));
          }, 0);

          setRecommendedDays(busiest || sportConfig.defaultTrainingDays);
        } else {
          setRecommendedDays(sportConfig.defaultTrainingDays);
        }
      } catch (error) {
        console.error("Error loading plan:", error);
        setSelectedPlan(null);
        setRecommendedDays(sportConfig.defaultTrainingDays);
      }
    };

    loadPlan();
  }, [formData.distance, formData.goalTime, sportConfig.sport, sportConfig.defaultTrainingDays]);

  const toggleTrainingDay = (day: string) => {
    setFormData((prev) => {
      const isSelected = prev.trainingDays.includes(day);
      const newTrainingDays = isSelected
        ? prev.trainingDays.filter((d) => d !== day)
        : [...prev.trainingDays, day];

      const newLongRunDay = newTrainingDays.includes(prev.longRunDay)
        ? prev.longRunDay
        : "";

      return { ...prev, trainingDays: newTrainingDays, longRunDay: newLongRunDay };
    });
  };

  const setLongRunDay = (day: string) => {
    setFormData((prev) => {
      const trainingDays = prev.trainingDays.includes(day)
        ? prev.trainingDays
        : [...prev.trainingDays, day];
      return { ...prev, trainingDays, longRunDay: day };
    });
  };

  const getGoalTimeOptions = () => {
    if (!formData.distance) return [];
    return sportConfig.goalTimes[formData.distance] || [];
  };

  const isFormValid = () => {
    const result = validateOnboarding({
      distance: formData.distance,
      goalTime: formData.goalTime,
      raceDate: formData.raceDate,
      trainingDays: formData.trainingDays,
      longRunDay: formData.longRunDay,
      selectedPlanLoaded: selectedPlan !== null,
      recommendedDays,
    });
    return result.valid;
  };

  const handleSubmit = () => {
    if (!selectedPlan || !formData.raceDate) {
      return;
    }

    if (formData.trainingDays.length < recommendedDays) {
      alert(
        `Please select at least ${recommendedDays} training days to match the busiest week in your plan.`,
      );
      return;
    }

    const resolvedLongRunDay =
      formData.longRunDay ||
      (formData.trainingDays.includes("Sunday")
        ? "Sunday"
        : formData.trainingDays.includes("Saturday")
          ? "Saturday"
          : formData.trainingDays[0] || "");

    const config = {
      planId: selectedPlan.id,
      raceDate: formData.raceDate.toISOString(),
      trainingDays: formData.trainingDays,
      longRunDay: resolvedLongRunDay,
      sport: sportConfig.sport,
    };

    localStorage.setItem("onboardingConfig", JSON.stringify(config));

    const params = new URLSearchParams({
      u: "km",
      p: selectedPlan.id,
      d: formData.raceDate.toISOString(),
      s: "1",
      sport: sportConfig.sport,
    });

    const td = encodeTrainingDays(formData.trainingDays as dayOfWeek[]);
    const lr = encodeLongRunDay(resolvedLongRunDay as dayOfWeek);
    if (td) params.set("td", td);
    if (lr) params.set("lr", lr);

    navigate(`/dashboard?${params.toString()}`);
  };

  return (
    <div className="rungenerate-container">
      <TopNav active={navKey as any} />

      <div className="rungenerate-content">
        {/* Hero Section with Scroll-based Images */}
        <div className="rungenerate-hero">
          <div className="hero-content">
            <div className="hero-image-wrapper">
              <img
                src={ONBOARDING_IMAGES[currentImageIndex]}
                alt={sportConfig.name}
                className="hero-image"
              />
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="rungenerate-form">
          <div className="form-container">
            {/* Step 1: Distance */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">1</span>
                <div>
                  <h2 className="section-title">Choose Your Distance</h2>
                  <p className="section-subtitle">{sportConfig.description}</p>
                </div>
              </div>

              <div className="options-grid">
                {sportConfig.distances.map((option) => (
                  <div
                    key={option.id}
                    className={`option-card ${formData.distance === option.id ? "selected" : ""}`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        distance: option.id,
                        goalTime: "",
                        trainingDays: [],
                        longRunDay: "",
                      }))
                    }
                  >
                    {option.icon && <div className="option-icon">{option.icon}</div>}
                    <h3 className="option-title">{option.label}</h3>
                    <p className="option-desc">{option.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 2: Goal Time */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">2</span>
                <div>
                  <h2 className="section-title">Choose Your Goal Time</h2>
                  <p className="section-subtitle">Pick the closest option</p>
                </div>
              </div>

              <div className="goal-time-grid">
                {getGoalTimeOptions().map((opt) => (
                  <div
                    key={opt.value}
                    className={`goal-time-card ${formData.goalTime === opt.value ? "selected" : ""}`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        goalTime: opt.value,
                        trainingDays: [],
                        longRunDay: "",
                      }))
                    }
                  >
                    <h3 className="goal-level">{opt.level}</h3>
                    <p className="goal-time">{opt.time}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 3: Race Date */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">3</span>
                <div>
                  <h2 className="section-title">Choose Your Event Date</h2>
                  <p className="section-subtitle">When is your event?</p>
                </div>
              </div>

              <div className="date-picker-container">
                <DatePicker
                  selected={formData.raceDate}
                  onChange={(date: Date | null) =>
                    setFormData((prev) => ({
                      ...prev,
                      raceDate: date,
                    }))
                  }
                  minDate={new Date()}
                  openToDate={defaultRaceDate}
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Click to select a date"
                  className="date-input"
                  inline
                />
              </div>
            </div>

            {/* Step 4: Training Schedule */}
            <div className="form-section">
              <div className="section-header">
                <span className="step-number">4</span>
                <div>
                  <h2 className="section-title">Set Your Training Schedule</h2>
                  <p className="section-subtitle">
                    Choose your training days and primary workout day
                  </p>
                  {recommendedDays > 0 && (
                    <p className="recommended-days">
                      Your plan has up to {recommendedDays} workout days
                      <br />
                      Selected {formData.trainingDays.length} / Required {recommendedDays}
                    </p>
                  )}
                </div>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">What days of the week do you train?</h3>
                {recommendedDays > 0 && (
                  <p className="section-subtitle" style={{ marginTop: 6 }}>
                    Select at least {recommendedDays} days to continue.
                  </p>
                )}
                <div className="days-grid">
                  {DAYS_OF_WEEK.map((day) => (
                    <div
                      key={day.full}
                      className={`day-card ${formData.trainingDays.includes(day.full) ? "selected" : ""}`}
                      onClick={() => toggleTrainingDay(day.full)}
                    >
                      {day.abbr}
                    </div>
                  ))}
                </div>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">What day is your key workout?</h3>
                <p className="section-subtitle" style={{ marginTop: 6 }}>
                  Optional — if you skip this, we'll pick a default.
                </p>
                <div className="days-grid">
                  {DAYS_OF_WEEK.map((day) => {
                    const isAvailable = formData.trainingDays.includes(day.full);
                    return (
                      <div
                        key={day.full}
                        className={`day-card ${formData.longRunDay === day.full ? "selected" : ""} ${!isAvailable ? "disabled" : ""} ${formData.longRunDay === day.full ? "long-run-selected" : ""}`}
                        onClick={() => isAvailable && setLongRunDay(day.full)}
                      >
                        {day.abbr}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="form-footer">
              <button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={!isFormValid()}
              >
                Create My Training Plan
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SportOnboarding;
